import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiUploadCloud, FiTrash2, FiFolder, FiMic, FiDownload } from 'react-icons/fi';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { puede } from '../utils/permisos';

function formatoFecha(fecha) {
  if (!fecha || !Number.isFinite(Date.parse(fecha))) return '—';
  return new Date(fecha).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

function idUsuario(valor) {
  return String(valor?._id || valor?.id || (typeof valor === 'string' ? valor : ''));
}

function nombreAutor(archivo) {
  return archivo.subidoPorNombre?.trim()
    || [archivo.subidoPor?.nombre, archivo.subidoPor?.apellidos].filter(Boolean).join(' ').trim()
    || archivo.subidoPor?.email
    || 'Usuario desconocido';
}

async function cargarBlob(archivo) {
  const { data } = await api.get(`/archivos/${archivo._id}`, { responseType: 'blob' });
  return data;
}

async function mensajeError(err, fallback) {
  let datos = err.response?.data;
  if (datos instanceof Blob) {
    try {
      const texto = typeof datos.text === 'function' ? await datos.text() : await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(datos);
      });
      datos = JSON.parse(texto);
    } catch {
      return fallback;
    }
  }
  return typeof datos?.error === 'string' ? datos.error : fallback;
}

export default function Archivos() {
  const { usuario } = useAuth();
  const [archivos, setArchivos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const [notificacion, setNotificacion] = useState('');
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [filtro, setFiltro] = useState('todos');
  const [descargando, setDescargando] = useState(null);
  const [errorDescarga, setErrorDescarga] = useState('');
  const [audio, setAudio] = useState({ id: null, url: '', error: '' });
  const montado = useRef(false);
  const urlsDescarga = useRef(new Set());

  const puedeSubir = puede(usuario, 'archivos', 'crear');
  const puedeBorrar = puede(usuario, 'archivos', 'eliminar');

  const cargarArchivos = async () => {
    try {
      const { data } = await api.get('/archivos');
      setArchivos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar los archivos');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    montado.current = true;
    const urls = urlsDescarga.current;
    cargarArchivos();
    return () => {
      montado.current = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  useEffect(() => {
    if (!archivoSeleccionado?.tipoMime?.startsWith('audio/')) return;
    let activo = true;
    let url;
    (async () => {
      try {
        const blob = await cargarBlob(archivoSeleccionado);
        if (!activo) return;
        url = URL.createObjectURL(blob);
        setAudio({ id: archivoSeleccionado._id, url, error: '' });
      } catch (err) {
        const mensaje = await mensajeError(err, 'No se pudo cargar el audio');
        if (activo) setAudio({ id: archivoSeleccionado._id, url: '', error: mensaje });
      }
    })();
    return () => {
      activo = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [archivoSeleccionado]);

  const cerrarAudio = () => {
    setArchivoSeleccionado(null);
    setAudio({ id: null, url: '', error: '' });
  };

  const descargarArchivo = async (archivo) => {
    setDescargando(archivo._id);
    setErrorDescarga('');
    let enlace;
    let url;
    try {
      const blob = await cargarBlob(archivo);
      if (!montado.current) return;
      url = URL.createObjectURL(blob);
      urlsDescarga.current.add(url);
      enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = archivo.nombreOriginal;
      document.body.appendChild(enlace);
      enlace.click();
    } catch (err) {
      const mensaje = await mensajeError(err, 'No se pudo descargar el archivo');
      if (montado.current) setErrorDescarga(mensaje);
    } finally {
      enlace?.remove();
      if (url) setTimeout(() => {
        if (urlsDescarga.current.delete(url)) URL.revokeObjectURL(url);
      }, 0);
      if (montado.current) setDescargando(null);
    }
  };

  const subirArchivo = async (file) => {
    if (!file) return;
    setSubiendo(true);
    setError('');
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result;
          await api.post('/archivos', {
            archivo: base64,
            nombreOriginal: file.name,
            tipoMime: file.type || 'application/octet-stream',
          });
          setNotificacion('Archivo subido correctamente');
          await cargarArchivos();
        } catch (err) {
          setError(err.response?.data?.error || 'No se pudo subir el archivo');
        } finally {
          setSubiendo(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo subir el archivo');
      setSubiendo(false);
    }
  };

  const borrarArchivo = async (archivo) => {
    if (!window.confirm(`¿Eliminar ${archivo.nombreOriginal}?`)) return;
    try {
      await api.delete(`/archivos/${archivo._id}`);
      setNotificacion('Archivo eliminado');
      await cargarArchivos();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo borrar el archivo');
    }
  };

  const esAudio = useMemo(() => (archivoSeleccionado?.tipoMime || '').startsWith('audio/'), [archivoSeleccionado]);
  const archivosVisibles = useMemo(() => {
    const usuarioId = idUsuario(usuario);
    const fechaOrden = (fecha) => Number.isFinite(Date.parse(fecha)) ? Date.parse(fecha) : -Infinity;
    return archivos
      .filter((archivo) => filtro === 'todos' || (usuarioId && idUsuario(archivo.subidoPor) === usuarioId))
      .slice()
      .sort((primero, segundo) => fechaOrden(segundo.createdAt) - fechaOrden(primero.createdAt));
  }, [archivos, filtro, usuario]);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Archivos de empresa</h1>
          <p className="page-subtitle">Una nube compartida para documentos y audios de la empresa.</p>
        </div>
      </div>

      {notificacion && <div className="alert-success" role="status">{notificacion}</div>}
      {error && <div className="alert-error" role="status">{error}</div>}
      {errorDescarga && <div className="alert-error" role="alert">{errorDescarga}</div>}
      {descargando !== null && <p role="status">Descargando archivo…</p>}

      {puedeSubir && (
        <div className="upload-box">
          <label className="upload-drop">
            <FiUploadCloud size={22} />
            <div>{subiendo ? 'Subiendo archivo…' : 'Sube un archivo o graba un audio desde tu dispositivo'}</div>
            <input type="file" accept="*/*" disabled={subiendo} onChange={(e) => subirArchivo(e.target.files?.[0])} style={{ display: 'none' }} />
          </label>
          <small>Los archivos quedan accesibles para todos los usuarios de la empresa.</small>
        </div>
      )}

      <label style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        Mostrar archivos
        <select value={filtro} onChange={(e) => setFiltro(e.target.value)}>
          <option value="todos">Todos</option>
          <option value="mios">Mis archivos</option>
        </select>
      </label>

      {cargando ? <p className="empty">Cargando archivos…</p> : archivosVisibles.length === 0 ? <p className="empty"><FiFolder /> {filtro === 'mios' ? 'Todavía no has subido archivos.' : 'No hay archivos compartidos todavía.'}</p> : (
        <div className="archivos-list">
          {archivosVisibles.map((archivo) => (
            <div key={archivo._id} className="archivo-card" style={{ flexWrap: 'wrap' }}>
              <div className="archivo-meta" style={{ minWidth: 0, flex: '1 1 220px', overflowWrap: 'anywhere' }}>
                <strong>{archivo.nombreOriginal}</strong>
                <small>{archivo.tipoMime} · {Math.round((archivo.tamano || 0) / 1024)} KB</small>
                <small>Subido por {nombreAutor(archivo)} · {formatoFecha(archivo.createdAt)}</small>
              </div>
              <div className="archivo-actions" style={{ flexWrap: 'wrap' }}>
                <button type="button" className="icon-btn" disabled={descargando !== null} aria-busy={descargando === archivo._id} title={descargando === archivo._id ? `Descargando ${archivo.nombreOriginal}` : `Descargar ${archivo.nombreOriginal}`} aria-label={`Descargar ${archivo.nombreOriginal}`} onClick={() => descargarArchivo(archivo)}><FiDownload /></button>
                {archivo.tipoMime?.startsWith('audio/') && (
                  <button className="btn" onClick={() => setArchivoSeleccionado(archivo)}><FiMic /> Reproducir</button>
                )}
                {puedeBorrar && (
                  <button className="icon-btn danger" onClick={() => borrarArchivo(archivo)}><FiTrash2 /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {archivoSeleccionado && (
        <div className="modal-overlay" onClick={cerrarAudio}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3 style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{archivoSeleccionado.nombreOriginal}</h3>
              <button className="modal-close" onClick={cerrarAudio} aria-label="Cerrar">×</button>
            </div>
            <div className="modal-body">
              {esAudio ? (audio.id === archivoSeleccionado._id && audio.error ? <p role="alert">{audio.error}</p> : audio.id === archivoSeleccionado._id && audio.url ? <audio controls src={audio.url} style={{ width: '100%' }} /> : <p role="status">Cargando audio…</p>) : <p>Este tipo de archivo no se puede reproducir aquí.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
